/**
 * Naming one cloud Environment from its Connections row (FOR-36371).
 *
 * The stored name and the label are deliberately not the same value. CMMD keeps
 * `displayName` NULL until somebody names the Environment, and a null name is
 * rendered from the owner's first name — so the field edits the stored name and
 * shows the derived label only as a placeholder. Pre-filling the derived label
 * would write it back on the next save and pin a name nobody chose.
 */
import { useCallback, useState } from "react";

import { CmmdApiError, cmmdApiClient } from "../../environments/cmmdApi";
import { sanitizePublicUiError } from "~/productSafeLabels";

/** CMMD's 409: this Seat already has an Environment under that name. */
export const ENVIRONMENT_NAME_TAKEN_MESSAGE =
  "You already have an environment with that name. Pick another.";

/** CMMD's own cap, mirrored so the field stops before the request does. */
export const ENVIRONMENT_NAME_MAX_LENGTH = 160;

export interface CloudEnvironmentRenameController {
  readonly isEditing: boolean;
  readonly isSaving: boolean;
  /** What is in the field. Empty means "derive from the owner". */
  readonly draft: string;
  /** The message shown beside the field, or null. Never a toast. */
  readonly error: string | null;
  readonly start: () => void;
  readonly cancel: () => void;
  readonly setDraft: (value: string) => void;
  readonly submit: () => Promise<void>;
}

/** `null` for an empty field, so a cleared name goes back to derived. */
function toStoredName(draft: string): string | null {
  const trimmed = draft.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function renameFailureMessage(error: unknown): string {
  if (error instanceof CmmdApiError && error.code === "environment_name_taken") {
    return ENVIRONMENT_NAME_TAKEN_MESSAGE;
  }
  return sanitizePublicUiError(error, "Could not rename this environment. Try again.");
}

export function useCloudEnvironmentRename(input: {
  readonly runtimeId: string;
  readonly displayName: string | null;
  /** Refetch the snapshot so the row, the label and the delete gate all move. */
  readonly onRenamed: () => void;
}): CloudEnvironmentRenameController {
  const { runtimeId, displayName, onRenamed } = input;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(() => {
    setDraft(displayName ?? "");
    setError(null);
    setIsEditing(true);
  }, [displayName]);

  const cancel = useCallback(() => {
    setIsEditing(false);
    setError(null);
    setDraft("");
  }, []);

  const submit = useCallback(async () => {
    const next = toStoredName(draft);
    // An unchanged name is not a write. Sending it would spend a round trip to
    // learn nothing, and a blank-to-blank edit would ask CMMD to clear a name
    // it never held.
    if (next === toStoredName(displayName ?? "")) {
      setIsEditing(false);
      setError(null);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await cmmdApiClient.renameCloudEnvironment(runtimeId, next);
      setIsEditing(false);
      onRenamed();
    } catch (failure) {
      // The field stays open holding what was typed: a clash is edited, not
      // retyped from scratch.
      setError(renameFailureMessage(failure));
    } finally {
      setIsSaving(false);
    }
  }, [displayName, draft, onRenamed, runtimeId]);

  return { isEditing, isSaving, draft, error, start, cancel, setDraft, submit };
}
